import React, { useMemo } from "react";

const PARAMETER_PRESETS = {
  cabinet: {
    width: 600,
    height: 720,
    depth: 560,
    boardThickness: 18,
    backThickness: 8,
    doorGap: 2,
  },
  board: {
    width: 600,
    height: 720,
    depth: 18,
    thickness: 18,
  },
  drawer: {
    width: 500,
    height: 120,
    depth: 500,
    frontGap: 2,
  },
  hardware: {
    offsetX: 0,
    offsetY: 0,
    offsetZ: 0,
  },
};

const COMMON_KEYS = [
  "width",
  "height",
  "depth",
  "thickness",
  "boardThickness",
  "backThickness",
  "doorGap",
  "frontGap",
  "offsetX",
  "offsetY",
  "offsetZ",
];

function toRows(obj = {}) {
  return Object.entries(obj).map(([key, value], index) => ({
    id: `${key}_${index}`,
    key,
    value: value ?? "",
  }));
}

function parseValue(value) {
  const raw = String(value ?? "").trim();
  if (raw === "") return "";

  const num = Number(raw);
  return Number.isFinite(num) ? num : value;
}

function toObject(rows = []) {
  const result = {};

  rows.forEach((row) => {
    const key = String(row.key || "").trim();
    if (!key) return;
    result[key] = parseValue(row.value);
  });

  return result;
}

function createEmptyRow() {
  return {
    id: `defparam_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    key: "",
    value: "",
  };
}

export default function DefinitionParametersInspector({
  parameters = {},
  onChange,
  title = "Definition Parameters",
}) {
  const rows = useMemo(() => toRows(parameters), [parameters]);

  function applyRows(nextRows) {
    onChange?.(toObject(nextRows));
  }

  function handleAdd() {
    applyRows([...(rows || []), createEmptyRow()]);
  }

  function handleRemove(rowId) {
    applyRows(rows.filter((row) => row.id !== rowId));
  }

  function handleChange(rowId, field, value) {
    applyRows(
      rows.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    );
  }

  function applyPreset(presetKey) {
    const preset = PARAMETER_PRESETS[presetKey] || {};
    onChange?.({
      ...parameters,
      ...preset,
    });
  }

  function clearAll() {
    const confirmed = window.confirm("Clear all definition parameters?");
    if (!confirmed) return;
    onChange?.({});
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">{title}</h3>
          <div className="text-xs text-gray-500">
            Default values used by this definition and inherited by child
            instances.
          </div>
        </div>

        <button
          type="button"
          onClick={handleAdd}
          className="px-3 py-2 rounded bg-blue-600 text-white text-sm"
        >
          + Add
        </button>
      </div>

      <datalist id="definition-parameter-keys">
        {COMMON_KEYS.map((key) => (
          <option key={key} value={key} />
        ))}
      </datalist>

      <div className="border rounded p-3 bg-gray-50 space-y-2">
        <div className="text-sm font-semibold">Presets</div>

        <div className="flex flex-wrap gap-2">
          {Object.keys(PARAMETER_PRESETS).map((presetKey) => (
            <button
              key={presetKey}
              type="button"
              onClick={() => applyPreset(presetKey)}
              className="px-3 py-1 rounded border bg-white text-xs capitalize"
            >
              {presetKey}
            </button>
          ))}
        </div>
      </div>

      {!rows.length ? (
        <div className="border rounded p-4 bg-gray-50 text-sm text-gray-500">
          No definition parameters. Add width, height, depth, or use a preset.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.id} className="border rounded p-3 bg-white space-y-2">
              <div className="grid grid-cols-5 gap-2 items-end">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Parameter
                  </label>
                  <input
                    list="definition-parameter-keys"
                    value={row.key}
                    onChange={(e) =>
                      handleChange(row.id, "key", e.target.value)
                    }
                    placeholder="width"
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                </div>

                <div className="col-span-3">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Default value
                  </label>
                  <input
                    value={row.value}
                    onChange={(e) =>
                      handleChange(row.id, "value", e.target.value)
                    }
                    placeholder="600"
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div className="text-[11px] text-gray-400">
                  Numeric values are saved as numbers. Text remains text.
                </div>

                <button
                  type="button"
                  onClick={() => handleRemove(row.id)}
                  className="px-2 py-1 rounded border text-red-600 text-xs"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {rows.length ? (
        <button
          type="button"
          onClick={clearAll}
          className="w-full px-3 py-2 rounded border text-red-600 bg-white text-sm"
        >
          Clear parameters
        </button>
      ) : null}
    </div>
  );
}